import React, { useEffect, useState, useMemo, useCallback } from 'react';
import API_BASE_URL from '../config/api';
import { useAuth } from '../context/AuthContext';

const RedisMonitor = () => {
  const { user } = useAuth();
  const [keys, setKeys]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState('');
  const [downloading, setDownloading] = useState(null); // key being downloaded

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/admin/redis/keys`, {
        headers: { 'x-user-id': user?.sub },
      });
      const data = await res.json();
      if (data.success) setKeys(data.keys);
      else setError(data.error || 'Failed to load keys');
    } catch (err) {
      setError('Failed to load Redis keys');
    } finally {
      setLoading(false);
    }
  }, [user?.sub]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return keys;
    return keys.filter(k => k.key.toLowerCase().includes(q));
  }, [keys, filter]);

  const fileTs = () => {
    const d = new Date();
    const pad = (n, w = 2) => String(n).padStart(w, '0');
    return `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  };

  const safeKey = (key) => key.replace(/[^a-zA-Z0-9]/g, '_');

  const triggerDownload = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchStreamData = async (key) => {
    const res  = await fetch(`${API_BASE_URL}/api/admin/redis/stream?key=${encodeURIComponent(key)}`, {
      headers: { 'x-user-id': user?.sub },
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Failed to load stream data');
    return data.data;
  };

  const downloadCsv = async (key) => {
    setDownloading(key + ':csv');
    try {
      const data = await fetchStreamData(key);
      const ts   = fileTs();
      const rows = ['timestamp;price;volume;side;id;redis_raw_id'];
      for (const r of data) {
        rows.push(`${r.timestamp ?? ''};${r.price ?? ''};${r.volume ?? ''};${r.side ?? ''};${r.id};${r.redis_raw_id}`);
      }
      triggerDownload(rows.join('\n'), `${safeKey(key)}_${ts}.csv`, 'text/csv;charset=utf-8;');
    } catch (err) {
      alert(`CSV export failed: ${err.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const downloadJson = async (key) => {
    setDownloading(key + ':json');
    try {
      const data = await fetchStreamData(key);
      const ts   = fileTs();
      triggerDownload(JSON.stringify(data, null, 2), `${safeKey(key)}_${ts}.json`, 'application/json');
    } catch (err) {
      alert(`JSON export failed: ${err.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const CsvIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="8" y1="13" x2="16" y2="13"/>
      <line x1="8" y1="17" x2="16" y2="17"/>
    </svg>
  );

  const JsonIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6"/>
      <polyline points="8 6 2 12 8 18"/>
    </svg>
  );

  const SpinnerIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ animation: 'spin 1s linear infinite' }}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );

  const iconBtn = {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#8b949e', padding: '2px 4px', borderRadius: '4px',
    display: 'inline-flex', alignItems: 'center',
  };

  return (
    <div style={{ padding: '24px', color: '#e6edf3', fontFamily: 'inherit' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <h1 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>Redis Monitor</h1>
      <p style={{ color: '#8b949e', fontSize: '13px', marginBottom: '20px' }}>
        Trade stream keys (x:*) and their record counts.
      </p>

      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Filter keys..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{
            width: '320px', padding: '7px 12px', background: '#0d1117',
            border: '1px solid #30363d', borderRadius: '6px',
            color: '#e6edf3', fontSize: '13px', outline: 'none',
          }}
        />
        <button
          onClick={fetchKeys}
          disabled={loading}
          title="Refresh"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '7px 12px', background: '#21262d', border: '1px solid #30363d',
            borderRadius: '6px', color: '#e6edf3', fontSize: '13px', cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.5 : 1,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={loading ? { animation: 'spin 1s linear infinite' } : {}}>
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
          Refresh
        </button>
        {filter && (
          <span style={{ color: '#8b949e', fontSize: '12px' }}>
            {filtered.length} / {keys.length} keys
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ color: '#8b949e' }}>Loading...</div>
      ) : error ? (
        <div style={{ color: '#f85149' }}>{error}</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #30363d', color: '#8b949e', textAlign: 'left' }}>
              <th style={{ padding: '8px 12px', fontWeight: 500 }}>KEY</th>
              <th style={{ padding: '8px 12px', fontWeight: 500, textAlign: 'right', width: '90px' }}>RECORDS</th>
              <th style={{ padding: '8px 12px', fontWeight: 500, textAlign: 'center', width: '80px' }}>EXPORT</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ padding: '16px 12px', color: '#8b949e', textAlign: 'center' }}>
                  No keys found
                </td>
              </tr>
            ) : (
              filtered.map(({ key, count }) => {
                const isCsvLoading  = downloading === key + ':csv';
                const isJsonLoading = downloading === key + ':json';
                return (
                  <tr key={key} style={{ borderBottom: '1px solid #21262d' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#161b22'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '7px 12px', fontFamily: 'monospace', color: '#e6edf3' }}>
                      {key}
                    </td>
                    <td style={{ padding: '7px 12px', textAlign: 'right', color: count === 0 ? '#484f58' : '#e6edf3' }}>
                      {count}
                    </td>
                    <td style={{ padding: '7px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button
                        style={{ ...iconBtn, color: isCsvLoading ? '#8b949e' : '#58a6ff' }}
                        onClick={() => !downloading && downloadCsv(key)}
                        title="Download CSV"
                        disabled={!!downloading}
                      >
                        {isCsvLoading ? <SpinnerIcon /> : <CsvIcon />}
                      </button>
                      <button
                        style={{ ...iconBtn, color: isJsonLoading ? '#8b949e' : '#3fb950', marginLeft: '4px' }}
                        onClick={() => !downloading && downloadJson(key)}
                        title="Download JSON"
                        disabled={!!downloading}
                      >
                        {isJsonLoading ? <SpinnerIcon /> : <JsonIcon />}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RedisMonitor;
